import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, Edit2, Trash2, Eye, EyeOff, Users, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useNavigate } from 'react-router-dom';

import { BACKEND_URL } from '../../config/api';
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('lms_token')}`
});

export default function CommunityManager({ user, onLogout }) {
  const navigate = useNavigate();
  const [communities, setCommunities] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCommunity, setEditingCommunity] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  const [formData, setFormData] = useState({
    course_id: '',
    title: '',
    short_description: '',
    details: '',
    hero_image_url: '',
    tags: [],
    is_published: false
  });
  const [tagInput, setTagInput] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [communitiesRes, coursesRes] = await Promise.all([
        axios.get(`${API}/communities`, { headers: getAuthHeaders() }),
        axios.get(`${API}/courses`, { headers: getAuthHeaders() })
      ]);
      setCommunities(communitiesRes.data);
      setCourses(coursesRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load communities');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    setUploading(true);
    try {
      const res = await axios.post(`${API}/communities/upload-image`, formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      });
      setFormData(prev => ({ ...prev, hero_image_url: res.data.image_url }));
      toast.success('Image uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.course_id || !formData.title || !formData.short_description) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      if (editingCommunity) {
        await axios.put(`${API}/communities/${editingCommunity.id}`, formData, {
          headers: getAuthHeaders()
        });
        toast.success('Community updated successfully');
      } else {
        await axios.post(`${API}/communities`, formData, {
          headers: getAuthHeaders()
        });
        toast.success('Community created successfully');
      }
      
      setShowForm(false);
      setEditingCommunity(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save community');
    }
  };

  const handleEdit = (community) => {
    setEditingCommunity(community);
    setFormData({
      course_id: community.course_id,
      title: community.title,
      short_description: community.short_description,
      details: community.details || '',
      hero_image_url: community.hero_image_url || '',
      tags: community.tags || [],
      is_published: community.is_published
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure? This will delete all posts in this community.')) return;
    
    try {
      await axios.delete(`${API}/communities/${id}`, { headers: getAuthHeaders() });
      toast.success('Community deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete community');
    }
  };

  const togglePublish = async (community) => {
    try {
      await axios.put(`${API}/communities/${community.id}`, 
        { is_published: !community.is_published },
        { headers: getAuthHeaders() }
      );
      toast.success(community.is_published ? 'Community unpublished' : 'Community published');
      fetchData();
    } catch (error) {
      toast.error('Failed to update publish status');
    }
  };

  const resetForm = () => {
    setFormData({
      course_id: '',
      title: '',
      short_description: '',
      details: '',
      hero_image_url: '',
      tags: [],
      is_published: false
    });
    setTagInput('');
  };

  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const removeTag = (tag) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag)
    }));
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout} currentPage="community">
        <div className="p-8">Loading...</div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout} currentPage="community">
      <div className="p-6 lg:p-8 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl lg:text-4xl font-bold text-navy">Community Manager</h1>
            <p className="text-[#4D4D4D] mt-2 font-light">Create and manage course communities</p>
          </div>
          <Button
            onClick={() => {
              resetForm();
              setEditingCommunity(null);
              setShowForm(true);
            }}
            className="bg-primary-blue hover:bg-[#1874CD] text-white font-semibold rounded-xl transition-smooth btn-press"
          >
            <Plus className="w-4 h-4 mr-2" strokeWidth={2} />
            Create Community
          </Button>
        </div>

        {communities.length === 0 ? (
          <Card className="border-0 card-shadow bg-white rounded-2xl">
            <CardContent className="p-12 text-center">
              <MessageCircle className="w-20 h-20 mx-auto mb-4 text-[#E7E7E7]" strokeWidth={1.5} />
              <h3 className="text-xl font-bold text-navy mb-2">No Communities Yet</h3>
              <p className="text-[#4D4D4D] font-light">Create your first course community to get started</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {communities.map((community) => (
              <Card key={community.id} className="border-0 card-shadow bg-white rounded-2xl card-hover transition-smooth overflow-hidden">
                {community.hero_image_url && (
                  <div className="h-32 overflow-hidden">
                    <img 
                      src={`${BACKEND_URL}${community.hero_image_url}`} 
                      alt={community.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-navy mb-1">{community.title}</h3>
                      <p className="text-sm text-[#4D4D4D] mb-2">{community.course_title}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      community.is_published 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {community.is_published ? 'Published' : 'Draft'}
                    </span>
                  </div>
                  
                  <p className="text-sm text-[#4D4D4D] mb-4 line-clamp-2">{community.short_description}</p>
                  
                  {community.tags && community.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-4">
                      {community.tags.map(tag => (
                        <span key={tag} className="px-2 py-1 bg-blue-50 text-primary-blue text-xs rounded-md">
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}
                  
                  <div className="flex items-center space-x-2 text-sm text-[#A5A5A5] mb-4">
                    <Users className="w-4 h-4" strokeWidth={2} />
                    <span>{community.post_count || 0} posts</span>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(community)}
                      className="border-2 border-[#E7E7E7] rounded-xl transition-smooth"
                    >
                      <Edit2 className="w-4 h-4 mr-1" strokeWidth={2} />
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/community/${community.id}`)}
                      className="border-2 border-primary-blue text-primary-blue hover:bg-blue-50 rounded-xl transition-smooth"
                    >
                      <Eye className="w-4 h-4 mr-1" strokeWidth={2} />
                      View
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => togglePublish(community)}
                      className="border-2 border-[#E7E7E7] rounded-xl transition-smooth"
                    >
                      {community.is_published ? <EyeOff className="w-4 h-4 mr-1" strokeWidth={2} /> : <Eye className="w-4 h-4 mr-1" strokeWidth={2} />}
                      {community.is_published ? 'Unpublish' : 'Publish'}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(community.id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 border-2 border-red-200 rounded-xl"
                    >
                      <Trash2 className="w-4 h-4 mr-1" strokeWidth={2} />
                      Delete
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Create/Edit Community Dialog */}
        <Dialog open={showForm} onOpenChange={setShowForm}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-navy">
                {editingCommunity ? 'Edit Community' : 'Create Community'}
              </DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <label className="block text-sm font-semibold text-navy mb-2">Course *</label>
                <Select 
                  value={formData.course_id} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, course_id: value }))}
                >
                  <SelectTrigger className="h-12 border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue">
                    <SelectValue placeholder="Select a course" />
                  </SelectTrigger>
                  <SelectContent>
                    {courses.map(course => (
                      <SelectItem key={course.id} value={course.id}>
                        {course.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-navy mb-2">Community Title *</label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., Course Discussions & Doubts"
                  className="h-12 border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-navy mb-2">Short Description / Teaser *</label>
                <Textarea
                  value={formData.short_description}
                  onChange={(e) => setFormData(prev => ({ ...prev, short_description: e.target.value }))}
                  placeholder="3-4 short bullet points or teaser text (shown on cards)"
                  rows={3}
                  className="border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-navy mb-2">Details / Body (Optional)</label>
                <Textarea
                  value={formData.details}
                  onChange={(e) => setFormData(prev => ({ ...prev, details: e.target.value }))}
                  placeholder="Longer description or community guidelines"
                  rows={4}
                  className="border-2 border-[#E7E7E7] rounded-xl focus:border-primary-blue"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-navy mb-2">Hero Image (Recommended: 1200×600)</label>
                <Input
                  type="file"
                  accept="image/jpeg,image/jpg,image/png"
                  onChange={handleImageUpload}
                  className="h-12 border-2 border-[#E7E7E7] rounded-xl"
                  disabled={uploading}
                />
                {formData.hero_image_url && (
                  <div className="mt-2">
                    <img 
                      src={`${BACKEND_URL}${formData.hero_image_url}`} 
                      alt="Preview"
                      className="w-full h-32 object-cover rounded-xl"
                    />
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-navy mb-2">Tags (Optional)</label>
                <div className="flex space-x-2 mb-2">
                  <Input
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                    placeholder="e.g., doubts, projects, resources"
                    className="h-10 border-2 border-[#E7E7E7] rounded-xl"
                  />
                  <Button 
                    type="button"
                    onClick={addTag}
                    className="bg-primary-blue hover:bg-[#1874CD] text-white rounded-xl"
                  >
                    Add
                  </Button>
                </div>
                {formData.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {formData.tags.map(tag => (
                      <span 
                        key={tag} 
                        className="px-3 py-1 bg-blue-50 text-primary-blue text-sm rounded-md flex items-center space-x-2 cursor-pointer"
                        onClick={() => removeTag(tag)}
                      >
                        <span>#{tag}</span>
                        <span className="text-xs">×</span>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="is_published"
                  checked={formData.is_published}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_published: e.target.checked }))}
                  className="w-4 h-4 text-primary-blue rounded"
                />
                <label htmlFor="is_published" className="text-sm font-medium text-navy">
                  Publish immediately (students will see this community)
                </label>
              </div>

              <div className="flex space-x-3 pt-4">
                <Button
                  type="submit"
                  disabled={uploading}
                  className="flex-1 bg-primary-blue hover:bg-[#1874CD] text-white font-semibold py-3 rounded-xl transition-smooth btn-press"
                >
                  {editingCommunity ? 'Update Community' : 'Create Community'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowForm(false)}
                  className="flex-1 border-2 border-[#E7E7E7] rounded-xl"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}